import { usePosCatalogQuery } from '@/api/queries/pos';
import type { PosCatalogItem } from '@/api/models';
import type { PosProduct, PosVariant } from './posProduct';

/**
 * Groups the catalog's per-variant rows into the per-product tiles the grid
 * renders. The endpoint already orders rows by product name then variant, so
 * products come out in that order and each product's variants keep theirs —
 * this only has to preserve first-seen order, never re-sort.
 *
 * A product's tile price is the lowest of its variants (what "from ₾X" would
 * mean), and its stock is the sum of theirs, so a tile reads as out of stock
 * only when every variant is. Which variant a sale books against is not
 * decided here: the tile adds a sole variant and opens the picker for several.
 */
export const groupCatalog = (items: PosCatalogItem[]): PosProduct[] => {
    const byProduct = new Map<string, PosProduct>();

    for (const item of items) {
        const variant: PosVariant = {
            variantId: item.variantId,
            label: item.variantLabel,
            barcode: item.barcode,
            alternateBarcodes: item.alternateBarcodes ?? [],
            price: item.price,
            sellableQuantity: item.sellableQuantity,
        };

        const existing = byProduct.get(item.productId);
        if (!existing) {
            byProduct.set(item.productId, {
                id: item.productId,
                name: item.productName,
                sku: item.sku ?? null,
                categoryId: item.categoryId,
                variants: [variant],
                price: variant.price,
                stock: variant.sellableQuantity,
            });
            continue;
        }

        existing.variants.push(variant);
        existing.price = Math.min(existing.price, variant.price);
        existing.stock += variant.sellableQuantity;
    }

    return [...byProduct.values()];
};

/**
 * Loads the sellable catalog for the signed-in branch from `GET /pos/catalog`
 * and groups it into tiles. A refusal surfaces as `isError` so the panel can
 * show an inline message instead of throwing at the boundary.
 *
 * `truncated` is the server saying its row cap was reached, so what is on
 * screen is not the whole catalog. It is passed through rather than swallowed:
 * a silently short catalog is exactly the bug this endpoint replaced.
 */
export const useProductCatalog = () => {
    const { data, isError, isPending } = usePosCatalogQuery();

    return {
        products: data ? groupCatalog(data.items) : [],
        isLoading: isPending,
        isError,
        truncated: data?.truncated ?? false,
    };
};
